import React, { useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import AILightComponent from "../../components/AILightComponent";

const PostFindItLocal = (moduleCalled) => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState?.user?._id || null;
  const token = authState?.token;
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [myPosts, setMyPosts] = useState([]);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editMessage, setEditMessage] = useState("");

  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);

  const addDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  };

  const [form, setForm] = useState({
    category: "Anything",
    title: "",
    briefDesc: "",
    tags: "",
    city: authState?.user?.city || "",
    area: authState?.user?.area || "",
    state: authState?.user?.state || "",
    country: authState?.user?.country || "",
    pincode: authState?.user?.pinCode || "",
    contactPerson: authState?.user?.name || "",
    mobileNumber: authState?.user?.mobile || "",
    whatsappNumber: authState?.user?.mobile || "",
    emailId: authState?.user?.email || "",
    callDays: "All Days",
    callTiming: "09:00-21:00",
    externalLink: "",
    dateExpires: addDays(30),
    streakOffer: "10% Discount on Streaks",
    vibe: "Good",
    isActive: "Yes",
  });

  const [message, setMessage] = useState("");

  const loadMyPosts = async () => {
    try {
      if (showMyPosts) {
        setShowMyPosts(false);
        return;
      }

      if (myPosts.length > 0) {
        setShowMyPosts(true);
        return;
      }

      const res = await fetchData({
        url: "/pd/finditlocal/myposted-list",
        method: "post",
        data: {
          userId: userId,
        },
        headers: { Authorization: token },
      });

      if (res?.status) {
        setMyPosts(res.records || []);
        setShowMyPosts(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async () => {
    try {
      const requiredFields = [
        "category",
        "title",
        "briefDesc",
        "tags",
        "city",
        "area",
        "state",
        "country",
        "pincode",
        "contactPerson",
        "mobileNumber",
        "whatsappNumber",
        "emailId",
        "callDays",
        "callTiming",
        "dateExpires",
        "streakOffer",
        "vibe",
      ];

      for (let field of requiredFields) {
        if (!form[field] || form[field].toString().trim() === "") {
          setMessage(`❌ ${field} is required`);
          return;
        }
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const expiry = new Date(form.dateExpires);
      expiry.setHours(0, 0, 0, 0);

      if (expiry < today) {
        setMessage("❌ Expiry date cannot be before today");
        return;
      }

      const payload = {
        ...form,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };

      const res = await fetchData({
        url: "/pd/finditlocal/create",
        method: "post",
        data: payload,
        headers: { Authorization: token },
      });

      if (res?.status) {
        setMessage("✅ FindItLocal Posted Successfully");
        setIsSubmitted(true);
        setForm({
          category: "Anything",
          title: "",
          briefDesc: "",
          tags: "",
          city: authState?.user?.city || "",
          area: authState?.user?.area || "",
          state: authState?.user?.state || "",
          country: authState?.user?.country || "",
          pincode: authState?.user?.pinCode || "",
          contactPerson: authState?.user?.name || "",
          mobileNumber: authState?.user?.mobile || "",
          whatsappNumber: authState?.user?.mobile || "",
          emailId: authState?.user?.email || "",
          callDays: "All Days",
          callTiming: "09:00-21:00",
          externalLink: "",
          dateExpires: addDays(30),
          streakOffer: "10% Discount on Streaks",
          vibe: "Good",
        });
      } else {
        setMessage("❌ Failed to post FindItLocal");
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Server Error");
    }
  };

  const editPost = (item) => {
    setEditingId(item.id);
    setEditMessage("✏️ Editing your FindItLocal posting");
    setForm({
      category: item.category,
      title: item.title,
      briefDesc: item.briefDesc,
      tags: Array.isArray(item.tags) ? item.tags.join(", ") : item.tags,
      city: item.city,
      area: item.area,
      state: item.state,
      country: item.country,
      pincode: item.pincode,
      contactPerson: item.contactPerson,
      mobileNumber: item.mobileNumber,
      whatsappNumber: item.whatsappNumber,
      emailId: item.emailId,
      callDays: item.callDays,
      callTiming: item.callTiming,
      externalLink: item.externalLink,
      dateExpires: item.dateExpires?.substring(0, 10),
      streakOffer: item.streakOffer,
      vibe: item.vibe,
      isActive: item.isActive ? "Yes" : "No",
    });
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditMessage("");
    setIsSubmitted(false);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (num) => /^[6-9]\d{9}$/.test(num.replace(/[^\d]/g, ""));
  const isValidWhatsApp = (num) =>
    /^[0-9]{10,15}$/.test(num.replace(/[^\d]/g, ""));

  return (
    <div className="space-y-3 pt-3 bg-[#0b0f19] p-3 rounded-xl">
      <div className="text-cyan-300 font-bold text-sm">
        🚀 Post a FinfItLocal for Listing
      </div>
      <div className="pt-2">
        <button
          onClick={loadMyPosts}
          className="w-fit px-2 py-1 bg-cyan-700 hover:bg-cyan-600 text-white text-xs rounded"
        >
          {showMyPosts ? "Hide My Posting List" : "📋 My Posting List"}
        </button>
      </div>
      {showMyPosts && (
        <div className="mt-2 border-y-2 border-gray-600 rounded-xl py-2">
          <div className="space-y-1 lg:hidden">
            {myPosts.map((item, index) => (
              <div
                key={item.id}
                className="bg-gray-800 rounded-lg px-3 py-1 border border-gray-700"
              >
                <div className="flex justify-between items-center">
                  <div className="text-cyan-300 font-semibold text-xs">
                    #{index + 1} {item.title}
                  </div>
                </div>
                <div className="mt-1 flex justify-between text-[11px] text-gray-400">
                  <div>
                    <span className="text-gray-100">
                      📅 {item.datePosted?.substring(0, 10)}
                    </span>
                    <span
                      className={
                        item.isActive ? "text-green-400" : "text-red-400"
                      }
                    >
                      {item.isActive ? "🟢 Active" : "🔴 Inactive"}
                    </span>
                  </div>
                  <div>
                    <button
                      onClick={() => editPost(item)}
                      className="text-xs lg:text-sm w-fit px-1 py-1 bg-transparent"
                      title="Edit"
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-cyan-700">
                  <th className="px-2 py-1">SN</th>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Active</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {myPosts.map((item, index) => (
                  <tr key={item.id} className="border-b border-gray-700">
                    <td className="px-2 py-1">{index + 1}</td>
                    <td>{item.datePosted?.substring(0, 10)}</td>
                    <td>{item.title}</td>
                    <td
                      className={
                        item.isActive ? "text-green-400" : "text-red-400"
                      }
                    >
                      {item.isActive ? "Yes" : "No"}
                    </td>
                    <td>
                      <button
                        onClick={() => editPost(item)}
                        className="text-cyan-400 text-base"
                        title="Edit"
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <select
        className="w-fit px-2 py-1 bg-gray-800 text-white text-xs rounded"
        value={form.category}
        onChange={(e) => setForm({ ...form, category: e.target.value })}
      >
        {[
          "Home Food",
          "Regional",
          "Fresh Today",
          "Gen Z",
          "Study",
          "Gen Alpha",
          "Local Deals",
          "Streak Offers",
          "Anything",
        ].map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>

      <input
        className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded"
        placeholder="Title (min 25 characters)"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
      />

      <textarea
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="Quick Product Description (min 25 chars, max 500)"
        value={form.briefDesc}
        maxLength={500}
        style={{ height: "150px" }}
        onChange={(e) => setForm({ ...form, briefDesc: e.target.value })}
      />

      <button
        onClick={toggleGenAIHelp}
        className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
          enableGenAIHelp
            ? "bg-purple-500 text-white"
            : "bg-[#111827] text-purple-300 border border-purple-500/30"
        }`}
      >
        {enableGenAIHelp ? "🙈 Hide GenAI" : "🤖 Ask GenAI"}
      </button>

      {enableGenAIHelp && (
        <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
          <AILightComponent
            mode="post_for_local_classified"
            param1={"🎯 Smart Classified Creating Assistant"}
            param2={`
Current Form Values:

Category: ${form.category}
Title: ${form.title}
Product Description: ${form.briefDesc}
Tags: ${form.tags}
City: ${form.city}
Pincode: ${form.pincode}
Contact Person: ${form.contactPerson}
Mobile Number: ${form.mobileNumber}
WhatsApp Number: ${form.whatsappNumber}
Email ID: ${form.emailId}
Call Days: ${form.callDays}
Call Timing: ${form.callTiming}
External Link: ${form.externalLink}
Post Valid Till: ${form.dateExpires}

Act as a Smart Local Classified Creation Assistant.

Review all the above details.

If the information is incomplete, infer the user's intent from the available content and generate a complete, attractive classified listing.

Your objectives:
- Improve the Title for maximum attention.
- Rewrite the Product Description to be clear, persuasive and trustworthy.
- Suggest SEO-friendly Tags.
- Suggest a better Category if appropriate.
- Identify missing or weak information.
- Improve readability and conversion.
- Do not invent or modify contact details.
- Keep the classified suitable for local buyers.
- Return the response in clearly labelled sections.
`}
          />
        </div>
      )}

      <input
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="Tags (comma separated)"
        value={form.tags}
        onChange={(e) => setForm({ ...form, tags: e.target.value })}
      />

      {/* LOCATION */}
      <div className="grid grid-cols-2 gap-2">
        <input
          disabled
          className="w-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded"
          placeholder="City"
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />

        <input
          disabled
          className="w-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded"
          placeholder="Pincode"
          value={form.pincode}
          onChange={(e) => setForm({ ...form, pincode: e.target.value })}
        />
      </div>

      {/* CONTACT PERSON */}
      <input
        disabled
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="Contact Person (min 3 chars)"
        value={form.contactPerson}
        onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
      />

      {/* PHONE */}
      <input
        disabled
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="Mobile Number"
        value={form.mobileNumber}
        onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })}
      />

      {/* WHATSAPP */}
      <input
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="WhatsApp Number"
        value={form.whatsappNumber}
        onChange={(e) => setForm({ ...form, whatsappNumber: e.target.value })}
      />

      {/* EMAIL */}
      <input
        disabled
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="Email ID"
        value={form.emailId}
        onChange={(e) => setForm({ ...form, emailId: e.target.value })}
      />

      {/* CALL DAYS */}
      <input
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="Call Days (e.g. Monday,Tuesday OR All Days)"
        value={form.callDays}
        onChange={(e) => setForm({ ...form, callDays: e.target.value })}
      />

      {/* CALL TIMING */}
      <input
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="Call Timing (e.g. 10:00-18:00)"
        value={form.callTiming}
        onChange={(e) => setForm({ ...form, callTiming: e.target.value })}
      />

      {/* EXTERNAL LINK */}
      <input
        className="w-full p-2 bg-gray-800 text-white text-xs rounded"
        placeholder="External Link (optional)"
        value={form.externalLink}
        onChange={(e) => setForm({ ...form, externalLink: e.target.value })}
      />

      {/* EXPIRY */}
      <label className="text-xs mt-1">Post Valid Till:</label>
      <input
        type="date"
        className="w-fit px-2 py-1 bg-gray-800 text-white text-xs rounded"
        value={form.dateExpires}
        onChange={(e) => setForm({ ...form, dateExpires: e.target.value })}
      />

      {/* SUBMIT */}
      <button
        disabled={isSubmitted}
        onClick={handleSubmit}
        className="bg-cyan-500 text-black text-xs font-bold w-fit px-2 py-1 rounded"
      >
        🚀 Submit FindItLocal
      </button>

      {message && <div className="text-xs text-yellow-300">{message}</div>}
    </div>
  );
};

export default PostFindItLocal;
